// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AckAlarmArgs, AlarmDetailsDto, AlarmEventState, AlarmNotification, GetAlarmByIdArgs, GetAllAlarmsArgs, GetObjectAlarmsArgs } from './types'
import { HubConnection } from './alarmHub'
import { apiConfig } from '../../config'
import { getHeaders, ApiScope } from '../silentAuth'
import { Recipe } from '@reduxjs/toolkit/dist/query/core/buildThunks'

// Define a service using a base URL and expected endpoints
export const alarmApi = createApi({
  reducerPath: 'alarmApi',
  tagTypes: ['Alarms'],
  refetchOnReconnect: true,
  baseQuery: fetchBaseQuery({
    baseUrl: apiConfig.alarmApiUrl,
    prepareHeaders: async (headers, { getState }) => { return await getHeaders(ApiScope, headers); }
  }),
  endpoints: (builder) => ({
    ackAlarmById: builder.mutation<AlarmDetailsDto, AckAlarmArgs>({
      query: ({ alarmId, resourceGroupId }) => ({
        url: `/${resourceGroupId}/${alarmId}/ack`,
        method: 'post'
      }),
      //invalidatesTags: ['Alarms'], we get a signalR notification - so we should not need this
    }),
    ackAlarms: builder.mutation<any, { resourceGroupId: string, alarmId: string}[] >({
      async queryFn(args, _queryApi, _extraOptions, baseQuery) {
        const promises = args.map((elm) => {
          return baseQuery({ 
            url: `/${elm.resourceGroupId}/${elm.alarmId}/ack`,
            method: 'post',
           });
        });
        return Promise.all(promises).then((results) => {
          return {
            data: results?.map((result, index) => {
              return result.data ?
                result.data as any
                : { error: result.error }
            })
          }; // final result must have either the property "data" or the property "error"
        });
      },
      //invalidatesTags: ['Alarms'], we get a signalR notification - so we should not need this
    }),
    ackAllAlarms: builder.mutation<number, void>({
      query: () => ({
        url: `/ack`,
        method: 'post'
      }),
      //invalidatesTags: ['Alarms'], we get a signalR notification - so we should not need this
    }),
    getAlarmById: builder.query<AlarmDetailsDto, GetAlarmByIdArgs>({
      query: ({ alarmId, resourceGroupId }) => `/${resourceGroupId}/${alarmId}`,
      providesTags: ['Alarms'],
    }),
    getAllAlarms: builder.query<AlarmDetailsDto[], GetAllAlarmsArgs>({
      query: (args: GetAllAlarmsArgs) => {
        return {
          url: `/all?pageSize=1000&start=${args.timeFrom}&end=${args.timeTo}`,
          method: 'get',
        };
      },
      transformResponse: (response: { data: AlarmDetailsDto[] }, meta, arg) => response.data,
      providesTags: ['Alarms'],
    }),
    getActiveObjectAlarms: builder.query<AlarmDetailsDto[], GetObjectAlarmsArgs>({
      query: ({ objectId, resourceGroupId, includeChildren }) => {
        return {
          url: `/object/${resourceGroupId}/${objectId}?pageSize=100${includeChildren ? '&includeChildren' : ''}`,
          method: 'get',
        };
      },
      transformResponse: (response: { data: AlarmDetailsDto[] }, meta, arg) => response.data,
      providesTags: ['Alarms'],
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        // create a websocket connection when the cache subscription starts
        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded

          HubConnection.on('AlarmAck', (msg: AlarmNotification) => onAlarmAck(updateCachedData, msg));
          HubConnection.on('AlarmOn', (msg: AlarmNotification) => onAlarmOnByObjectId(updateCachedData, msg, arg.objectId));
          HubConnection.on('AlarmOff', (msg: AlarmNotification) => onAlarmOff(updateCachedData, msg));

        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
      },
    }),
    getActiveAlarms: builder.query<AlarmDetailsDto[], any | void>({
      query: () => `?pageSize=1000`,
      providesTags: ['Alarms'],
      transformResponse: (response: { data: AlarmDetailsDto[] }, meta, arg) => response.data,
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {

        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded

          HubConnection.on('AlarmAck', (msg: AlarmNotification) => onAlarmAck(updateCachedData, msg));
          HubConnection.on('AlarmOn', (msg: AlarmNotification) => onAlarmOn(updateCachedData, msg));
          HubConnection.on('AlarmOff', (msg: AlarmNotification) => onAlarmOff(updateCachedData, msg));

        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
      },
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAlarmByIdQuery,
  useGetActiveAlarmsQuery,
  useGetActiveObjectAlarmsQuery,
  useAckAlarmByIdMutation,
  useAckAllAlarmsMutation,
  useGetAllAlarmsQuery,
  useAckAlarmsMutation
} = alarmApi


function onAlarmOn(updateCachedData: Recipe<any>, msg: any) {

  const { state, alarm } = msg;
  updateCachedData((draft) => {
    console.debug("alarmApi.onAlarmOn() add Alarm " + alarm.id, alarm);
    draft.push((alarm as any))
  })
}

function onAlarmOnByObjectId(updateCachedData: Recipe<any>, msg: any, objectId: string) {

  const { state, alarm } = msg;
  if (alarm.objectId !== objectId)
    return;
  updateCachedData((draft) => {
    console.debug("alarmApi.onAlarmOnByObjectId() add Alarm " + alarm.id, alarm);
    draft.push((alarm as any))
  })
}


function onAlarmOff(updateCachedData: Recipe<any>,  msg: any) {

  const { state, alarm } = msg;
  updateCachedData((draft) => {
    if (state === AlarmEventState.Deleted) // alarm removed
    {
      updateCachedData((draft) => {
        const index = draft.findIndex((a) => a.id === alarm.id);
        if (index > -1) {
          console.debug("alarmApi.onAlarmOff() remove Alarm " + alarm.id);
          draft.splice(index, 1);
        }
      })
    }
    else {
      updateCachedData((draft) => {
        const elm = draft.find((a) => a.id === alarm.id);
        if (elm) {
          console.debug("alarmApi.onAlarmOff() update Alarm " + alarm.id, alarm);
          elm.offTime = alarm.offTime;
        }
      })
    }
  })
}

function onAlarmAck(updateCachedData: Recipe<any>, { state, alarm }: AlarmNotification) {

  if (state === AlarmEventState.Deleted) // alarm removed
  {
    updateCachedData((draft) => {
      const index = draft.findIndex((a) => a.id === alarm.id);
      if (index > -1) {
        draft.splice(index, 1);
        console.debug("alarmApi.onAlarmAck() remove Alarm " + alarm.id, alarm);
      }
    })
  }
  else {
    updateCachedData((draft) => {

      const elm = draft.find((a) => a.id === alarm.id);
      if (elm) {
        console.debug("alarmApi.onAlarmAck() update Alarm " + alarm.id, alarm);
        elm.ackTime = alarm.ackTime;
        elm.ackUser = alarm.ackUser;
        elm.ackUserId = alarm.ackUserId;
      }
    })
  }
}